import React, { useMemo, useRef, useState } from "react";
import { FaCheck, FaFileAlt, FaMagic, FaPaperclip, FaRobot, FaTimes } from "react-icons/fa";
import api from "../api";
import "./DocumentTemplateAiAssistant.css";

const errorMessage = (error) =>
  error?.response?.data?.error || error?.message || "Não foi possível gerar o documento com IA.";


export default function DocumentTemplateAiAssistant({
  target = "student",
  template,
  variables = [],
  onApply,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const availableVariables = useMemo(() =>
    (Array.isArray(variables) ? variables : [])
      .map((variable) => ({
        id: String(variable?.id || "").trim(),
        label: String(variable?.label || variable?.id || "").trim(),
        group: String(variable?.group || variable?.section || variable?.category || "").trim(),
      }))
      .filter((variable) => variable.id), [variables]);

  const chooseFile = (event) => {
    const next = event.target.files?.[0] || null;
    setFile(next);
    setError("");
    setSuccess("");
  };

  const clearFile = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const generate = async () => {
    if (!prompt.trim() && !file) {
      setError("Escreva o que deseja criar ou anexe um documento para a IA usar como base.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const body = new FormData();
      body.append("prompt", prompt.trim());
      body.append("variables", JSON.stringify(availableVariables));
      if (file) {
        body.append("file", file);
      } else if (template) {
        body.append("currentTemplate", JSON.stringify({
          name: template.name || "",
          title: template.title || "",
          content: template.content || "",
        }));
      }

      const response = await api.post(`/ai/document-templates/${target}/generate`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const generated = response.data?.template;
      if (!generated?.name || !generated?.title || !generated?.content) {
        throw new Error("A resposta da IA não trouxe um modelo de documento completo.");
      }
      onApply?.(generated);
      setSuccess(
        response.data?.mode === "editable_replica"
          ? `Documento “${file?.name || "anexado"}” copiado por inteiro, formatado e aplicado ao editor para você poder alterar.`
          : response.data?.mode === "strict_replica"
            ? `Documento “${file?.name || "anexado"}” lido por inteiro e aplicado ao editor.`
          : file
            ? `Documento “${file.name}” transformado em modelo e aplicado ao editor.`
            : "Modelo gerado pela Nash IA e aplicado ao editor.",
      );
    } catch (generationError) {
      setError(errorMessage(generationError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={open ? "document-template-ai document-template-ai--open" : "document-template-ai"}>
      <button
        type="button"
        className="document-template-ai__toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className="document-template-ai__toggle-icon"><FaRobot /></span>
        <span>
          <strong>Criar com IA</strong>
        </span>
        <FaMagic className="document-template-ai__spark" />
      </button>

      {open && (
        <div className="document-template-ai__body">
          <label className="document-template-ai__prompt">
            <span>O que você quer criar ou alterar?</span>
            <textarea
              rows="4"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Ex.: Crie uma declaração de matrícula formal. Use nome completo, CPF, número de matrícula, turma e data atual."
            />
          </label>

          <div className="document-template-ai__file-row">
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.txt,.docx,.xlsx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={chooseFile}
              hidden
            />
            <button
              type="button"
              className="document-template-ai__attach"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              <FaPaperclip /> {file ? "Trocar documento" : "Anexar documento"}
            </button>
            {file ? (
              <span className="document-template-ai__file">
                <FaFileAlt />
                <span title={file.name}>{file.name}</span>
                <button type="button" onClick={clearFile} aria-label="Remover documento"><FaTimes /></button>
              </span>
            ) : null}
          </div>

          {error && <div className="document-template-ai__message document-template-ai__message--error">{error}</div>}
          {success && <div className="document-template-ai__message document-template-ai__message--success"><FaCheck /> {success}</div>}

          <div className="document-template-ai__footer">
            <button type="button" className="document-template-ai__generate" onClick={generate} disabled={loading}>
              <FaMagic /> {loading ? "Gerando modelo..." : "Gerar e aplicar no documento"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
